import { Dispatch, SetStateAction, useCallback } from "react"
import { useField, useFormikContext } from "formik"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    CreateUpdateBookingOrganizer,
    SelectedMainUserState,
} from "../../createOrderTypes"
import { CreateUpdateBookingFormValues } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { generateConfirmBookingInitialValues, bookingFormValuesToDynamicConfigKey } from "../form/helpers"
import { NullableCalendarEvent } from "./calendar/fullCalendarTypes"
import { CreateUpdateBookingDialogWrap } from "./CreateUpdateBookingDialogWrap"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"

import {
    BookingConfigurationResponse,
    ConfigQuery,
    CreateUpdateBookingResponse,
    createBooking,
    fetchBookingConfiguration,
    updateBooking,
} from "../../createOrderApi"
import { CreateUpdateBooking } from "../../createOrderTypes"
import type { DynamicMainFormValues } from "../form/formTypes"
import { mergeValuesWithConfig } from "../form/mergeValuesWithConfig"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { OrderType } from "../../../../../modules/payments/orderTypes"
import { OrganizerType } from "../../../../types/OrganizerTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { buildFinalResources } from "./helpers"

const selectBookingConfig = (data: BookingConfigurationResponse) => data?.bookingConfig

type ResourceBookingConfirmDialogProps = Readonly<{
    open: boolean
    user: SelectedMainUserState
    orderId?: number
    initialEventStateId?: number | null
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    dialogType: CreateOrderDialogType
    orderDetails?: MeetingServiceOrder
    mode?: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    setConfirmBookingDialog: (val: boolean) => void
    handleOpenSuccessDialog: (details: CreateUpdateBookingResponse, content: CreateUpdateBooking) => void
    handleClearResourceIds: () => void
}>

export function ResourceBookingConfirmDialog({
    open,
    user,
    orderId,
    initialEventStateId,
    orderFormStatusRef,
    dialogType,
    orderDetails,
    mode,
    flowConfig,
    setOrderContext,
    setConfirmBookingDialog,
    handleOpenSuccessDialog,
    handleClearResourceIds,
}: ResourceBookingConfirmDialogProps) {
    const dateTimeFactory = useDateTime()
    const { isFullInitOrder, meetingServiceId, resourceBookingId } = orderFormStatusRef.current

    const { values, setFieldValue } = useFormikContext<DynamicMainFormValues>()
    const [{ value: event }] = useField<NullableCalendarEvent>("event")

    const { data: bookingConfig } = useApiQuery(fetchBookingConfiguration, {
        queryName: ConfigQuery.BOOKING_CONFIG,
        dependencies: { userId: user.userId },
        select: selectBookingConfig,
        enabled: !!event,
        useErrorBoundary: true,
    })

    const handleDialogClose = useCallback(
        (formValues?: CreateUpdateBookingFormValues) => {
            if (formValues) {
                const updatedFields = bookingFormValuesToDynamicConfigKey(formValues, values)
                Object.entries(updatedFields).forEach(([key, value]) => {
                    setFieldValue(key, value)
                })
            }

            setConfirmBookingDialog(false)
        },
        [values],
    )

    const { mutateAsync: callCreateBooking } = useApiMutation(createBooking, {
        async onSuccess(params, { order, calendarImportLinks }, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            orderFormStatusRef.current.resourceBookingId = order.id
            setOrderContext((prev) => {
                return { ...prev, orderId: order.id }
            })
            handleOpenSuccessDialog({ order, calendarImportLinks }, params.content)
        },
    })

    const { mutateAsync: callUpdateBooking } = useApiMutation(updateBooking, {
        async onSuccess(params, { order, calendarImportLinks }, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            order && handleOpenSuccessDialog({ order, calendarImportLinks }, params.content)
        },
    })
    if (!open || !bookingConfig || !event) {
        return null
    }

    const mergedValues = mergeValuesWithConfig({
        existing: values,
        config: bookingConfig,
        user,
        dateTimeFactory,
        dialogType,
        orderDetails,
        mode,
        flowConfig,
    })

    const handleSubmit = ({
        meetingDetails,
        resources,
        organizers,
        attendees,
        ...rest
    }: CreateUpdateBookingFormValues) => {
        const finalResources = buildFinalResources(meetingDetails.resourceIds, resources)
        // const organizersList: CreateUpdateBookingOrganizer[] = [
        //     {
        //         id: user.userId,
        //         organizerType: OrganizerType.CREATOR,
        //     },
        // ]

        const organizersList: CreateUpdateBookingOrganizer[] = []

        if (organizers?.id) {
            organizersList.push({
                id: organizers.id,
                organizerType: OrganizerType.ORGANIZER,
            })
        } else if (organizers?.name && organizers.name.trim()) {
            organizersList.push({
                organizerType: OrganizerType.ORGANIZER,
                name: organizers.name.trim(),
            })
        }

        const { resourceIds, startDateTime, endDateTime, ...restMeetingDetails } = meetingDetails

        const modifyValues = {
            orderType: OrderType.MEETING,
            ...rest,
            organizers: organizersList,
            meetingDetails: {
                ...restMeetingDetails,
                startDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(startDateTime)),
                endDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(endDateTime)),
                attendees,
                ...(flowConfig?.calendarAppointmentId && { calendarAppointmentId: flowConfig.calendarAppointmentId }),
            },
            bookingDetails: {
                resources: finalResources,
            },
        }

        if (isFullInitOrder || resourceBookingId) {
            let meetingId = initialEventStateId || orderId
            callUpdateBooking({ userId: user.userId, orderId: meetingId ?? 0, content: modifyValues })
        } else {
            callCreateBooking({
                userId: user.userId,
                content: {
                    ...modifyValues,
                    ...(meetingServiceId ? { order: { id: meetingServiceId } } : {}),
                },
            })
        }
    }

    return (
        <CreateUpdateBookingDialogWrap
            open={open}
            onClose={handleDialogClose}
            event={event}
            initialValues={generateConfirmBookingInitialValues(mergedValues, initialEventStateId)}
            resourcesList={mergedValues["fullResourcesList"]}
            handleSubmit={handleSubmit}
            handleClearResourceIds={handleClearResourceIds}
            user={user}
            orderId={isFullInitOrder ? orderId : resourceBookingId}
            bookingConfig={bookingConfig}
        />
    )
}
